import React, { useState, useEffect } from "react";
import "./NMRModal.scss";
import { FiX, FiCheck } from "react-icons/fi";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TextField, Autocomplete } from "@mui/material";
import interactWithUSComments from "../../config/liveFirebase";

const NMRModal = ({
  isOpen,
  onClose,
  projects,
  selectedProject: defaultProject,
}) => {
  const [selectedProject, setSelectedProject] = useState(defaultProject);
  const [contractors, setContractors] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset states when modal opens, but keep the default project
      setSelectedProject(defaultProject);
      setContractors([]);
      setSelectedContractors([]);
      setStartDate(null);
      setEndDate(null);
    }
  }, [isOpen, defaultProject]);

  useEffect(() => {
    if (selectedProject?.ProjectId) {
      fetchContractors();
    }
  }, [selectedProject]);

  const fetchContractors = async () => {
    if (!selectedProject?.ProjectId) return;

    setLoading(true);
    try {
      const response = await interactWithUSComments(
        "GET",
        `${selectedProject.ProjectId}/teamName`
      );
      if (response.status === "Success" && response.data) {
        const teamNames = Object.values(response.data);
        const uniqueTeamNames = [...new Set(teamNames)];
        setContractors(uniqueTeamNames);
      }
    } catch (error) {
      console.error("Error fetching contractors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContractorSelection = (contractor) => {
    setSelectedContractors((prev) => {
      if (prev.includes(contractor)) {
        return prev.filter((c) => c !== contractor);
      } else {
        return [...prev, contractor];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedContractors.length === contractors.length) {
      setSelectedContractors([]);
    } else {
      setSelectedContractors([...contractors]);
    }
  };

  const handleGenerate = () => {
    if (!selectedProject) {
      alert("Please select a project");
      return;
    }
    if (!startDate || !endDate) {
      alert("Please select date range");
      return;
    }
    if (selectedContractors.length === 0) {
      alert("Please select at least one contractor");
      return;
    }
    // TODO: Implement bill generation logic
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="nmr-modal-overlay">
      <div className="nmr-modal">
        <div className="nmr-modal-header">
          <h2>Generate NMR Bill</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <div className="nmr-modal-content">
          <div className="project-section">
            <h3>Select Project</h3>
            <Autocomplete
              options={projects || []}
              getOptionLabel={(option) => option.ClientName || ""}
              value={selectedProject}
              onChange={(event, newValue) => setSelectedProject(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select a project"
                  variant="outlined"
                  fullWidth
                />
              )}
              className="project-selector"
            />
          </div>

          <div className="date-range-section">
            <h3>Select Date Range</h3>
            <div className="date-inputs">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth variant="outlined" />
                  )}
                  className="date-picker"
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth variant="outlined" />
                  )}
                  className="date-picker"
                />
              </LocalizationProvider>
            </div>
          </div>

          <div className="contractors-section">
            <div className="contractors-header">
              <h3>Select Contractors</h3>
              <button
                className={`select-all-btn`}
                onClick={handleSelectAll}
                disabled={!selectedProject || loading}
              >
                {selectedContractors.length === contractors.length
                  ? "Deselect All"
                  : "Select All"}
              </button>
            </div>
            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Loading contractors...</p>
              </div>
            ) : !selectedProject ? (
              <div className="no-project-message">
                Please select a project to view contractors
              </div>
            ) : contractors.length === 0 ? (
              <div className="no-contractors-message">
                No contractors found for this project
              </div>
            ) : (
              <div className="contractors-list">
                {contractors.map((contractor, index) => (
                  <div
                    key={index}
                    className={`contractor-item ${
                      selectedContractors.includes(contractor) ? "selected" : ""
                    }`}
                    onClick={() => handleContractorSelection(contractor)}
                  >
                    <div className="checkbox">
                      {selectedContractors.includes(contractor) && (
                        <FiCheck size={16} />
                      )}
                    </div>
                    <span>{contractor}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="nmr-modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="generate-btn"
            onClick={handleGenerate}
            disabled={
              !selectedProject ||
              !startDate ||
              !endDate ||
              selectedContractors.length === 0
            }
          >
            Generate Bill
          </button>
        </div>
      </div>
    </div>
  );
};

export default NMRModal;
